import { useEffect, useState } from "react";

export interface Branding {
  productName: string;
  productLogoUrl: string | null;
  productMode: "workforce" | "playstudio";
}

/**
 * Branding is a config value (CH-CFG-3), not a build-time constant: baking it
 * into the bundle would force a Docker rebuild per tenant. It comes from
 * GET /api/config, which is unauthenticated so the login page can render it.
 *
 * Deliberately not react-query. The login page renders outside the
 * QueryClientProvider, and this value is fetched once per app load and never
 * changes, so a module-level cache is both simpler and more widely usable.
 */
const FALLBACK: Branding = {
  productName: "WorkForceOS",
  productLogoUrl: null,
  productMode: "workforce",
};

let cached: Branding | null = null;
let inflight: Promise<Branding> | null = null;
const subscribers = new Set<(b: Branding) => void>();

/**
 * Accept only a well-formed payload. A 200 carrying the wrong shape — a
 * stubbed fetch in a test, a proxy returning an error page as JSON — must
 * fall back rather than render `undefined` into the wordmark.
 */
function parse(v: unknown): Branding {
  if (!v || typeof v !== "object") return FALLBACK;
  const o = v as Record<string, unknown>;
  if (typeof o["productName"] !== "string" || o["productName"] === "") {
    return FALLBACK;
  }
  return {
    productName: o["productName"],
    productLogoUrl:
      typeof o["productLogoUrl"] === "string" ? o["productLogoUrl"] : null,
    productMode: o["productMode"] === "playstudio" ? "playstudio" : "workforce",
  };
}

function load(): Promise<Branding> {
  if (inflight) return inflight;
  inflight = fetch("/api/config", { credentials: "include" })
    .then((r) => (r.ok ? (r.json() as Promise<unknown>) : null))
    .then(parse)
    .catch(() => FALLBACK)
    .then((b) => {
      cached = b;
      for (const fn of subscribers) fn(b);
      return b;
    });
  return inflight;
}

export function useBranding(): Branding {
  const [branding, setBranding] = useState<Branding>(cached ?? FALLBACK);
  useEffect(() => {
    if (cached) {
      setBranding(cached);
      return;
    }
    subscribers.add(setBranding);
    void load();
    return () => {
      subscribers.delete(setBranding);
    };
  }, []);
  return branding;
}

/** Test seam — resets the module cache between suites. */
export function __resetBrandingCache(): void {
  cached = null;
  inflight = null;
  subscribers.clear();
}
